import { useCallback, useRef, useState, type ReactNode } from 'react';
import Modal from './Modal';
import { useSettings } from '../hooks/useSettings';
import {
  PersonAlreadyHasContact,
  PersonConfirmationRequired,
  createContact,
  type PersonCandidate,
  type PersonChoice,
} from '../lib/db';
import { recName, type Contact } from '../types';

type NewContact = Omit<Contact, 'id' | 'createdAt' | 'updatedAt' | 'tenantId'>;

export type PersonEntryResult =
  | { status: 'created'; contactId: string }
  /** The chosen Person already had a roster record; nothing was created. */
  | { status: 'exists'; contactId: string }
  | { status: 'cancelled' };

const REASON_LABEL: Record<PersonCandidate['reasons'][number], string> = {
  name: 'same name',
  similar_name: 'similar name',
  email: 'same email',
  phone: 'same phone',
};

type Answer = { kind: 'existing'; personId: string } | { kind: 'distinct' } | { kind: 'cancel' };

function CandidatesDialog({
  candidates,
  hiddenCount,
  onAnswer,
}: {
  candidates: PersonCandidate[];
  hiddenCount: number;
  onAnswer: (a: Answer) => void;
}) {
  const { settings } = useSettings();
  return (
    <Modal title="Is this someone already in Compass?" onClose={() => onAnswer({ kind: 'cancel' })}>
      <div className="subtitle" style={{ marginBottom: 8 }}>
        Compass found {candidates.length === 1 ? 'a person' : 'people'} who may be the same human. Matching a name, email or
        phone never decides this automatically — please choose.
      </div>
      <div className="drawer-checklist">
        {candidates.map((c) => (
          <div key={c.personId} className="checkbox-row" style={{ justifyContent: 'space-between', gap: 12 }}>
            <span>
              <b>{c.displayName}</b>
              {c.status === 'provisional' && <span className="subtitle"> · unconfirmed</span>}
              <div className="subtitle">
                {c.reasons.map((r) => REASON_LABEL[r]).join(', ')}
                {c.districtIds.length > 0 && ` · ${c.districtIds.map((d) => recName(settings.districts, d)).join(', ')}`}
              </div>
            </span>
            <button className="btn" onClick={() => onAnswer({ kind: 'existing', personId: c.personId })}>
              Use this person
            </button>
          </div>
        ))}
      </div>
      {hiddenCount > 0 && (
        <div className="subtitle" style={{ marginTop: 6 }}>
          {hiddenCount === 1 ? 'One more possible match is' : `${hiddenCount} more possible matches are`} outside your access. If you
          continue, an administrator will be asked to review.
        </div>
      )}
      <div className="actions">
        <button className="btn" onClick={() => onAnswer({ kind: 'cancel' })}>
          Cancel
        </button>
        <button className="btn primary" onClick={() => onAnswer({ kind: 'distinct' })}>
          This is a different person
        </button>
      </div>
    </Modal>
  );
}

/**
 * Person-first roster entry. Use `enter()` wherever a NEW roster Contact would be created:
 * it names the Person (a Member ID, an explicit Person, or a new human), shows the duplicate-candidate
 * confirmation when the server asks for one, and returns the resulting Contact. Render `dialog` once.
 */
export function usePersonEntry(): {
  enter: (fields: NewContact, choice?: PersonChoice) => Promise<PersonEntryResult>;
  dialog: ReactNode;
} {
  const [pending, setPending] = useState<{ candidates: PersonCandidate[]; hidden: number } | null>(null);
  const resolver = useRef<((a: Answer) => void) | null>(null);

  const ask = useCallback(
    (candidates: PersonCandidate[], hidden: number) =>
      new Promise<Answer>((resolve) => {
        resolver.current = (a) => {
          resolver.current = null;
          setPending(null);
          resolve(a);
        };
        setPending({ candidates, hidden });
      }),
    [],
  );

  const enter = useCallback(
    async (fields: NewContact, initial?: PersonChoice): Promise<PersonEntryResult> => {
      // One id per entry attempt, so a retry or a double submit can never create a second Person or Contact.
      const requestId = crypto.randomUUID().replace(/-/g, '');
      let choice: PersonChoice = initial ?? { create: true };
      for (;;) {
        try {
          return { status: 'created', contactId: await createContact(fields, choice, requestId) };
        } catch (e) {
          if (e instanceof PersonAlreadyHasContact) return { status: 'exists', contactId: e.contactId };
          if (e instanceof PersonConfirmationRequired) {
            const answer = await ask(e.candidates, e.hiddenCandidateCount);
            if (answer.kind === 'cancel') return { status: 'cancelled' };
            choice = answer.kind === 'existing'
              ? { personId: answer.personId }
              : { create: true, distinctFromPersonIds: e.candidates.map((c) => c.personId) };
            continue;
          }
          throw e;
        }
      }
    },
    [ask],
  );

  const dialog = pending ? (
    <CandidatesDialog
      candidates={pending.candidates}
      hiddenCount={pending.hidden}
      onAnswer={(a) => resolver.current?.(a)}
    />
  ) : null;

  return { enter, dialog };
}
